import React from 'react';
import moment from 'moment';
import PhotoPreviewer from 'components/Modal/PhotoPreviewer/PhotoPreviewer';
import { isEmpty } from 'ramda';
import { useLocation, useNavigate } from 'react-router-dom';
import { PAGE_TABS } from 'components/VisitRecords/VisitRecords';
import { usePatient } from '../../context/appContext';
import {
  capitalize,
  formatPhoneNumber,
  formatToFullAddress,
  inchesToFeetAndInches,
} from '../../utils/utils';

function About() {
  const { patient } = usePatient();
  const [showPhotoPreviewer, setShowPhotoPreviewer] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const getPatientDOB = () => {
    const dob =
      moment().diff(moment(patient.patientDetails?.dateOfBirth).format('YYYY-MM-DD'), 'years') || 0;

    if (dob) return dob;

    return '-';
  };

  const getPatientPlan = (plan: string) => {
    return plan === 'COMPLETE-CARE'
      ? 'Complete Care'
      : plan === 'VIRTUAL-CARE'
      ? 'Virtual Care'
      : plan === 'STARTER-CARE'
      ? 'Starter Care'
      : 'Primary Care';
  };

  const handleNextTab = () => {
    const indexOfActiveTab = PAGE_TABS.findIndex(fndTab => fndTab.id === 'about');

    const nextTab = PAGE_TABS.find((_, index) => index === indexOfActiveTab + 1);

    if (nextTab) {
      const tabURL = `${location.pathname}/${nextTab.id}${location.search}`;
      navigate(tabURL);
    }
  };

  if (!patient) return null;

  return (
    <>
      <div className="flex flex-col pt-0 pb-10 tablet:pt-6 tablet:pb-7">
        <div className="flex flex-col space-y-4 tablet:space-y-6">
          {/* Grid One */}
          <div className="grid grid-cols-1 gap-4 tablet:gap-0 tablet:grid-cols-2 laptop:grid-cols-4">
            <div>
              <span className="text-sm font-medium">Plan Type</span>
              <p className="text-[15px] text-stone-500">
                {patient?.patientDetails?.planType || 'River health'}
              </p>
            </div>

            <div>
              <span className="text-sm font-medium">Subscription Type</span>
              <p className="text-[15px] text-stone-500">
                {getPatientPlan(patient?.patientDetails?.subscriptionType)}
              </p>
            </div>

            <div className="flex flex-col space-y-4">
              <div>
                <span className="text-sm font-medium">Family Plan Type</span>
                <p className="text-[15px] text-stone-500">
                  {patient?.patientDetails?.planFamilyType}
                </p>
              </div>
            </div>

            <div className="hidden tablet:flex flex-col space-y-4">
              <div>
                <span className="text-sm font-medium">Identification</span>
                <p className="text-[15px] text-stone-500">
                  <button
                    onClick={() => setShowPhotoPreviewer(true)}
                    disabled={isEmpty(patient.idImage)}
                    type="button"
                    className="underline text-river-blue underline-offset-4 disabled:opacity-50"
                  >
                    Click to view
                  </button>
                </p>
              </div>
            </div>
          </div>

          {/* Grid Two */}
          <div className="grid grid-cols-1 gap-4 tablet:gap-0 tablet:grid-cols-2 laptop:grid-cols-4">
            <div>
              <span className="text-sm font-medium">Preferred Pronoun</span>
              <p className="text-[15px] text-stone-500">
                {capitalize(patient.preferredPronouns) || '-'}
              </p>
            </div>

            <div className="flex flex-col space-y-4">
              <div>
                <span className="text-sm font-medium">Preferred Name</span>
                <p className="text-[15px] text-stone-500">{patient.preferredName || '-'}</p>
              </div>
            </div>

            <div>
              <span className="text-sm font-medium">Gender at birth</span>
              <p className="text-[15px] text-stone-500">
                {capitalize(patient.patientDetails?.gender) || '-'}
              </p>
            </div>

            <div>
              <span className="text-sm font-medium">Gender Identity</span>
              <p className="text-[15px] text-stone-500 ">
                {capitalize(patient.patientDetails?.genderIdentity) || '-'}
              </p>
            </div>
          </div>

          {/* Grid Three */}
          <div className="grid grid-cols-1 gap-4 tablet:gap-0 tablet:grid-cols-2 laptop:grid-cols-4">
            <div>
              <span className="text-sm font-medium">Age</span>
              <p className="text-[15px] text-stone-500">
                {getPatientDOB()}{' '}
                <span>
                  (
                  {patient.patientDetails?.dateOfBirth
                    ? moment(patient.patientDetails?.dateOfBirth, 'YYYY-MM-DD').format(
                        'DD MMMM YYYY',
                      )
                    : ''}
                  )
                </span>
              </p>
            </div>

            <div>
              <span className="text-sm font-medium">Height</span>
              <p className="text-[15px] text-stone-500">
                {patient?.patientDetails?.height
                  ? `${inchesToFeetAndInches(patient.patientDetails?.height || 0).feet}'${
                      inchesToFeetAndInches(patient.patientDetails?.height || 0).inches
                    }"`
                  : '-'}
              </p>
            </div>

            <div className="flex flex-col space-y-4">
              <div>
                <span className="text-sm font-medium">Weight</span>
                <p className="text-[15px] text-stone-500">
                  {patient?.patientDetails?.weight ? `${patient?.patientDetails?.weight} lbs` : '-'}
                </p>
              </div>
            </div>

            <div>
              <span className="text-sm font-medium">Phone number</span>
              <p className="text-[15px] text-stone-500">
                {patient.phoneNumber ? formatPhoneNumber(patient.phoneNumber || '') : '-'}
              </p>
            </div>
          </div>

          {/* Grid Four */}
          <div className="grid grid-cols-1 gap-4 tablet:gap-0 tablet:grid-cols-2 laptop:grid-cols-4">
            <div className="flex flex-col space-y-4">
              <div>
                <span className="text-sm font-medium">Address</span>
                <p className="text-[15px] text-stone-500">{patient.streetAddress}</p>
                <p className="text-[15px] text-stone-500">
                  {formatToFullAddress([
                    '',
                    patient?.apartment,
                    patient?.city,
                    patient?.state,
                    `${patient?.zipCode || ''}`,
                  ])}
                </p>
              </div>
            </div>

            <div className="flex flex-col space-y-4">
              <div>
                <span className="text-sm font-medium">Zip code</span>
                <p className="text-[15px] text-stone-500">{patient.zipCode}</p>
              </div>
            </div>

            <div>
              <span className="text-sm font-medium">Email</span>
              <p className="text-[15px] text-stone-500">{patient.email || '-'}</p>
            </div>

            <div className="flex flex-col space-y-4">
              <div>
                <span className="text-sm font-medium">Emergency Contact</span>
                <p className="text-[15px] text-stone-500">
                  {patient?.patientDetails?.emergencyContactFirstName
                    ? `${patient?.patientDetails?.emergencyContactFirstName} ${patient?.patientDetails?.emergencyContactLastName}`
                    : '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Grid Five */}
          <div className="grid grid-cols-1 gap-4 tablet:gap-0 tablet:grid-cols-2 laptop:grid-cols-4">
            <div>
              <span className="text-sm font-medium">Emergency Number</span>
              <p className="text-[15px] text-stone-500">
                {patient?.patientDetails?.emergencyContactPhoneNumber
                  ? formatPhoneNumber(patient?.patientDetails?.emergencyContactPhoneNumber || '')
                  : '-'}
              </p>
            </div>

            {patient?.familyManager ? (
              <>
                <div>
                  <span className="text-sm font-medium">Family Manager Name</span>
                  <p className="text-[15px] text-stone-500">
                    {patient?.familyManager?.firstName} {patient?.familyManager?.lastName}
                  </p>
                </div>

                <div>
                  <span className="text-sm font-medium">Family Manager Phone</span>
                  <p className="text-[15px] text-stone-500">
                    {formatPhoneNumber(patient?.familyManager?.phoneNumber || '')}
                  </p>
                </div>
              </>
            ) : null}

            <div className="tablet:hidden flex flex-col space-y-4">
              <div>
                <span className="text-sm font-medium">Identification</span>
                <p className="text-[15px] text-stone-500">
                  <button
                    onClick={() => setShowPhotoPreviewer(true)}
                    disabled={isEmpty(patient.idImage)}
                    type="button"
                    className="underline text-river-blue underline-offset-4 disabled:opacity-50"
                  >
                    Click to view
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => handleNextTab()}
        className="border border-river-blue text-river-blue rounded-full px-5 py-[9px] text-sm mt-6 min-w-[180px] 
        hover:bg-[#EEFBFF]"
      >
        Next
      </button>

      {showPhotoPreviewer && Boolean(patient.idImage) && (
        <PhotoPreviewer
          photos={[patient.idImage]}
          onCloseModal={() => setShowPhotoPreviewer(false)}
        />
      )}
    </>
  );
}

export default About;
